import React from 'react'
import { QueryClient, QueryClientProvider, DefaultOptions } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

// Default options for React Query
const defaultOptions: DefaultOptions = {
  queries: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (except 401 which is handled by interceptors)
      if (error?.statusCode >= 400 && error?.statusCode < 500 && error?.statusCode !== 401) {
        return false
      }
      // Retry up to 3 times for other errors
      return failureCount < 3
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true
  },
  mutations: {
    retry: (failureCount, error: any) => {
      // Don't retry mutations on client errors
      if (error?.statusCode >= 400 && error?.statusCode < 500) {
        return false
      }
      // Retry up to 2 times for server errors
      return failureCount < 2
    }
  }
}

// Create query client instance
export const queryClient = new QueryClient({
  defaultOptions
})

// Query client provider component
interface QueryProviderProps {
  children: React.ReactNode
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Only show devtools in development */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools
          initialIsOpen={false}
        />
      )}
    </QueryClientProvider>
  )
}

// Helper function to invalidate all cached data
export const invalidateAllQueries = () => {
  queryClient.invalidateQueries()
}

// Helper function to clear all cached data
export const clearAllQueries = () => {
  queryClient.clear()
}

// Helper functions for manual cache management
export const setCacheData = (queryKey: any[], data: any) => {
  queryClient.setQueryData(queryKey, data)
}

export const getCacheData = (queryKey: any[]) => {
  return queryClient.getQueryData(queryKey)
}

export const invalidateQueries = (queryKey: any[]) => {
  queryClient.invalidateQueries({ queryKey })
}

export const removeQueries = (queryKey: any[]) => {
  queryClient.removeQueries({ queryKey })
}

// Prefetch helper
export const prefetchQuery = async (queryKey: any[], queryFn: () => Promise<any>) => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn
  })
}

export default queryClient